import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion, type HTMLMotionProps } from 'motion/react';
import { Loader2 } from 'lucide-react';

/** Buttons, cards, badges and the other small pieces every screen is built from. */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'quiet';
export type ButtonSize = 'sm' | 'md' | 'lg';

type BaseButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
  block?: boolean;
  children?: ReactNode;
};

function buttonClass({ variant = 'primary', size = 'md', block, className }: BaseButtonProps & { className?: string }) {
  return ['btn', `btn-${variant}`, `btn-${size}`, block ? 'btn-block' : '', className || '']
    .filter(Boolean)
    .join(' ');
}

export const Button = forwardRef<HTMLButtonElement, BaseButtonProps & ButtonHTMLAttributes<HTMLButtonElement>>(
  function Button({ variant, size, loading, icon, iconRight, block, children, className, disabled, ...rest }, ref) {
    return (
      <button
        ref={ref}
        className={buttonClass({ variant, size, block, className })}
        disabled={disabled || loading}
        {...rest}
      >
        {loading ? <Loader2 className="btn-spin" size={16} aria-hidden /> : icon}
        {children ? <span className="btn-label">{children}</span> : null}
        {iconRight}
      </button>
    );
  },
);

export function ButtonLink({
  to,
  href,
  variant,
  size,
  icon,
  iconRight,
  block,
  children,
  className,
  ...rest
}: BaseButtonProps & { to?: string; href?: string; className?: string; target?: string; rel?: string; onClick?: () => void }) {
  const cls = buttonClass({ variant, size, block, className });
  if (to) {
    return (
      <Link to={to} className={cls} {...rest}>
        {icon}
        <span className="btn-label">{children}</span>
        {iconRight}
      </Link>
    );
  }
  return (
    <a href={href} className={cls} {...rest}>
      {icon}
      <span className="btn-label">{children}</span>
      {iconRight}
    </a>
  );
}

export function IconButton({
  label,
  children,
  className,
  ...rest
}: { label: string } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className={['icon-btn', className || ''].join(' ')} aria-label={label} title={label} {...rest}>
      {children}
    </button>
  );
}

export type CardProps = {
  children: ReactNode;
  className?: string;
  /** Lifts on hover — for cards that are themselves a link or button. */
  interactive?: boolean;
  padded?: boolean;
} & Omit<HTMLMotionProps<'div'>, 'children'>;

export function Card({ children, className, interactive, padded = true, ...rest }: CardProps) {
  return (
    <motion.div
      className={['card', interactive ? 'card-interactive' : '', padded ? '' : 'card-flush', className || '']
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function CardHeader({
  title,
  lead,
  actions,
  eyebrow,
}: {
  title: ReactNode;
  lead?: ReactNode;
  actions?: ReactNode;
  eyebrow?: ReactNode;
}) {
  // A title on its own line is a single line of type, and actions beside it
  // read as crooked unless the two are centred on each other (the owner's
  // report, 6 Oct 2026: "not alligned and badly layedout"). As soon as there
  // is a lead or an eyebrow the copy becomes a block, and the actions have to
  // align to its first line instead — centring them against the whole block
  // drops them halfway down the paragraph.
  const stacked = Boolean(lead || eyebrow);
  return (
    <div className={`card-header${stacked ? ' card-header-stacked' : ''}`}>
      <div className="card-header-copy">
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h2>{title}</h2>
        {lead ? <p className="muted card-header-lead">{lead}</p> : null}
      </div>
      {actions ? <div className="card-header-actions">{actions}</div> : null}
    </div>
  );
}

export type Tone = 'neutral' | 'accent' | 'good' | 'warning' | 'serious' | 'critical' | 'info';

export function Badge({ children, tone = 'neutral', icon }: { children: ReactNode; tone?: Tone; icon?: ReactNode }) {
  return (
    <span className={`badge badge-${tone}`}>
      {icon}
      {children}
    </span>
  );
}

export function Pill({ children, tone = 'neutral' }: { children: ReactNode; tone?: Tone }) {
  return <span className={`pill pill-${tone}`}>{children}</span>;
}

export function Stat({
  label,
  value,
  hint,
  tone,
  icon,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
  icon?: ReactNode;
}) {
  return (
    <div className={['stat-tile', tone ? `stat-${tone}` : ''].filter(Boolean).join(' ')}>
      <div className="stat-top">
        <span className="stat-label">{label}</span>
        {icon ? <span className="stat-icon">{icon}</span> : null}
      </div>
      <div className="stat-value numeric">{value}</div>
      {hint ? <div className="stat-hint subtle">{hint}</div> : null}
    </div>
  );
}

export function Skeleton({ height = 16, width, radius = 'var(--radius-sm)' }: { height?: number; width?: number | string; radius?: string }) {
  return <span className="skeleton" style={{ height, width: width ?? '100%', borderRadius: radius }} aria-hidden />;
}

export function SkeletonCard({ lines = 3 }: { lines?: number }) {
  return (
    <div className="card skeleton-card" aria-hidden>
      <Skeleton height={20} width="42%" />
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} height={12} width={`${88 - i * 12}%`} />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  lead,
  action,
  icon,
}: {
  title: ReactNode;
  lead?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="empty-state">
      {icon ? <div className="empty-icon">{icon}</div> : null}
      <p className="empty-title">{title}</p>
      {lead ? <p className="muted empty-lead">{lead}</p> : null}
      {action ? <div className="empty-action">{action}</div> : null}
    </div>
  );
}

export function Divider({ label }: { label?: ReactNode }) {
  if (!label) return <hr className="divider" />;
  return (
    <div className="divider-labelled">
      <span>{label}</span>
    </div>
  );
}

export function InlineError({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p className="inline-error" role="alert">
      {children}
    </p>
  );
}

/** `ledger`: a plain ink-on-paper note for a condition of the workspace
 * itself (a demo, a trial) — never a coloured wash, and never a state the
 * record is in. Everything else is a `Tone`, the same states Badge/Pill use. */
export type BannerTone = Tone | 'ledger';

export function Banner({
  tone = 'accent',
  title,
  children,
  action,
  onDismiss,
}: {
  tone?: BannerTone;
  title?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
  onDismiss?: () => void;
}) {
  return (
    <div className={`banner banner-${tone}`} role="status">
      <div className="banner-copy">
        {title ? <strong>{title}</strong> : null}
        <span>{children}</span>
      </div>
      <div className="banner-actions">
        {action}
        {onDismiss ? (
          <button type="button" className="banner-dismiss" onClick={onDismiss} aria-label="Dismiss">
            ×
          </button>
        ) : null}
      </div>
    </div>
  );
}
